import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    alpha,
    useTheme,
    CircularProgress,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    Container,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Language as LanguageIcon,
    History as HistoryIcon,
} from '@mui/icons-material';
import { trendingApi } from '../../services/api';
import PageAlert from '../PageAlert';
import { COUNTRIES, PLATFORMS } from './constants';
import TrendCard from './TrendCard';

const TrendingNow = () => {
    const theme = useTheme();
    const [trends, setTrends] = useState([]);
    const [loading, setLoading] = useState(true);
    const [region, setRegion] = useState('united_states');
    const [platform, setPlatform] = useState('web');
    const [error, setError] = useState(null);

    const fetchTrends = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await trendingApi.getTrending(region, platform);
            setTrends(data || []);
        } catch (err) {
            console.error('Failed to fetch trends:', err);
            setError('Could not connect to Google Trends. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, [region, platform]);

    useEffect(() => {
        fetchTrends();
    }, [fetchTrends]);

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header Section */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', lg: 'flex-end' },
                gap: 4,
                mb: 8
            }}>
                <Box sx={{ maxWidth: { lg: '50%' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '20px',
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontSize: '0.75rem',
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            Live Intelligence
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <HistoryIcon sx={{ fontSize: 14 }} /> Updated just now
                        </Typography>
                    </Box>
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 900,
                            letterSpacing: '-0.04em',
                            mb: 1.5,
                            fontSize: { xs: '2.5rem', md: '3.5rem' },
                            background: `linear-gradient(45deg, ${theme.palette.text.primary}, ${alpha(theme.palette.text.primary, 0.7)})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Market Trends
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, lineHeight: 1.6 }}>
                        Discover what the world is searching for. Use these real-time signals to create content that resonates.
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'center',
                    width: { xs: '100%', lg: 'auto' },
                    justifyContent: { xs: 'flex-start', lg: 'flex-end' }
                }}>
                    {/* Platform Toggles */}
                    <ToggleButtonGroup
                        value={platform}
                        exclusive
                        onChange={(e, next) => next && setPlatform(next)}
                        sx={{
                            bgcolor: alpha(theme.palette.background.paper, 0.5),
                            borderRadius: '16px',
                            p: 0.5,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            width: { xs: '100%', sm: 'auto' },
                            display: 'flex',
                            '& .MuiToggleButton-root': {
                                border: 'none',
                                borderRadius: '12px',
                                px: { xs: 1, sm: 2 },
                                py: 1,
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                color: 'text.secondary',
                                textTransform: 'none',
                                gap: 1,
                                flex: { xs: 1, sm: 'none' },
                                '&.Mui-selected': {
                                    bgcolor: theme.palette.primary.main,
                                    color: '#fff',
                                    '&:hover': { bgcolor: theme.palette.primary.dark }
                                },
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                            }
                        }}
                    >
                        {PLATFORMS.map((p) => (
                            <ToggleButton key={p.code} value={p.code}>
                                <span>{p.icon}</span>
                                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{p.name}</Box>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' }, alignItems: 'center' }}>
                        <FormControl
                            variant="standard"
                            sx={{
                                flex: { xs: 1, sm: 'none' },
                                minWidth: { xs: 'none', sm: 200 },
                                '& .MuiInput-root': {
                                    p: '10px 16px',
                                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                                    borderRadius: '16px',
                                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    '&:hover': {
                                        bgcolor: theme.palette.background.paper,
                                        borderColor: alpha(theme.palette.primary.main, 0.3),
                                    },
                                    '&:before, &:after': { display: 'none' },
                                }
                            }}
                        >
                            <Select
                                value={region}
                                onChange={(e) => setRegion(e.target.value)}
                                renderValue={(selected) => {
                                    const country = COUNTRIES.find(c => c.code === selected);
                                    return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Typography sx={{ fontSize: '1.2rem' }}>{country?.flag}</Typography>
                                            <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>
                                                {country?.name}
                                            </Typography>
                                        </Box>
                                    );
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            borderRadius: 3,
                                            mt: 1,
                                            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                                            border: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                                        }
                                    }
                                }}
                            >
                                {COUNTRIES.map((c) => (
                                    <MenuItem key={c.code} value={c.code} sx={{ py: 1.5, mx: 1, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Typography sx={{ fontSize: '1.2rem' }}>{c.flag}</Typography>
                                            <Typography sx={{ fontWeight: 600 }}>{c.name}</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <IconButton
                            onClick={() => fetchTrends()}
                            disabled={loading}
                            sx={{
                                p: 1.5,
                                borderRadius: '16px',
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                color: theme.palette.primary.main,
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            {error && <PageAlert error={error} />}

            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 15 }}>
                    <CircularProgress thickness={6} size={50} sx={{ color: theme.palette.primary.main, mb: 3 }} />
                    <Typography sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.8rem' }}>
                        Syncing with Global Trends...
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {trends.map((item, index) => (
                        <TrendCard key={index} item={item} index={index} />
                    ))}
                </Grid>
            )}

            {/* Footer Insights */}
            <Box sx={{
                mt: 12,
                p: 6,
                borderRadius: 8,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.05)}`,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                gap: 4,
                textAlign: { xs: 'center', md: 'left' }
            }}>
                <Box sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '24px',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <LanguageIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 900, mb: 1, letterSpacing: '-0.02em' }}>
                        Strategic Advantage
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 800 }}>
                        These topics represent high-velocity search interest. Content built around these terms typically experiences 3x higher organic reach within the first 24 hours of publication.
                    </Typography>
                </Box>
            </Box>
        </Container>
    );
};

export default TrendingNow;
